import { useState } from 'react';
import { apiUpdateVariant, type Variant, type VariantUpdate } from '~/lib/api/shop';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Section,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import { StockControl } from './StockControl';
import {
  apiErrorMessage,
  formatMinor,
  minorToMajorInput,
  normalizeSku,
  parseMoney,
  parseOptionalMoney,
  variantStockLabel,
  variantTitle,
} from './shop-model';

export function VariantsTable({
  appId,
  variants,
  currency,
  skuPattern,
  onVariantChanged,
}: {
  appId: string;
  variants: Variant[];
  currency: string;
  skuPattern: string;
  onVariantChanged: (next: Variant) => void;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const live = variants.filter((v) => !v.archivedAt);
  const archived = variants.filter((v) => v.archivedAt);

  return (
    <Section title={`Variants (${live.length})`} icon="i-ph:squares-four">
      <p className="text-[11px] leading-relaxed text-muted">
        New SKUs follow <code className="font-geist-mono text-secondary">{skuPattern}</code>. Tokens:{' '}
        <code className="font-geist-mono">{'{PRODUCT} {OPTION1} {OPTION2} {OPTION3} {N}'}</code>.
      </p>
      <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
        {live.map((v) => (
          <VariantRow key={v.id} appId={appId} variant={v} currency={currency} onChanged={onVariantChanged} />
        ))}
      </div>

      {archived.length > 0 && (
        <div className="space-y-2">
          <button type="button" onClick={() => setShowArchived((s) => !s)} className={BTN_GHOST}>
            <div className={classNames(showArchived ? 'i-ph:caret-down' : 'i-ph:caret-right', 'text-[12px]')} />
            {archived.length} archived combination{archived.length === 1 ? '' : 's'}
          </button>
          {showArchived && (
            <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden opacity-70')}>
              {archived.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-[12px] text-secondary">{variantTitle(v.options)}</div>
                    <div className="font-geist-mono text-[10px] text-faint">{v.sku}</div>
                  </div>
                  <span className="font-geist-mono text-[11px] text-muted">{formatMinor(v.price, currency)}</span>
                </div>
              ))}
              <p className="px-4 py-2 text-[11px] leading-relaxed text-muted">
                Kept for past orders and stock history. Add the value back to an option to revive one.
              </p>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

function VariantRow({
  appId,
  variant,
  currency,
  onChanged,
}: {
  appId: string;
  variant: Variant;
  currency: string;
  onChanged: (next: Variant) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<'save' | 'backorder' | 'untrack' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stock = variantStockLabel(variant);
  const tracked = variant.stock !== null;

  const update = async (kind: 'backorder' | 'untrack', patch: VariantUpdate) => {
    setBusy(kind);
    setError(null);

    try {
      onChanged(await apiUpdateVariant(appId, variant.id, patch));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update this variant.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-[13px] text-primary">{variantTitle(variant.options)}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 font-geist-mono text-[10px] text-muted">
            <span>{variant.sku}</span>
            {variant.barcode && (
              <>
                <span aria-hidden>·</span>
                <span>{variant.barcode}</span>
              </>
            )}
          </div>
          <div className={classNames('mt-0.5 text-[11px]', stock.tone === 'danger' ? 'text-danger' : 'text-muted')}>
            {stock.label}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-geist-mono text-[13px] text-primary">{formatMinor(variant.price, currency)}</div>
          {variant.compareAtPrice !== null && variant.compareAtPrice > variant.price && (
            <div className="font-geist-mono text-[11px] text-faint line-through">
              {formatMinor(variant.compareAtPrice, currency)}
            </div>
          )}
        </div>
      </div>

      {editing ? (
        <VariantEditForm
          appId={appId}
          variant={variant}
          currency={currency}
          onDone={(next) => {
            setEditing(false);

            if (next) {
              onChanged(next);
            }
          }}
        />
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          <button type="button" onClick={() => setEditing(true)} className={BTN_GHOST}>
            <div className="i-ph:pencil-simple text-[13px]" />
            Edit
          </button>
          <StockControl appId={appId} variant={variant} onChanged={onChanged} />
          {tracked && (
            <button
              type="button"
              onClick={() => void update('untrack', { trackStock: false })}
              disabled={busy !== null}
              className={BTN_GHOST}
              title="Stop counting stock; the history is kept"
            >
              {busy === 'untrack' && <Spinner />}
              Stop tracking
            </button>
          )}
        </div>
      )}

      {tracked && !editing && (
        <div className="mt-2">
          <Switch
            checked={variant.allowBackorder}
            onChange={(next) => void update('backorder', { allowBackorder: next })}
            disabled={busy !== null}
            label="Keep selling when out of stock"
          />
        </div>
      )}

      {error && (
        <div className="mt-2">
          <ErrorBanner message={error} />
        </div>
      )}
    </div>
  );
}

function VariantEditForm({
  appId,
  variant,
  currency,
  onDone,
}: {
  appId: string;
  variant: Variant;
  currency: string;
  onDone: (next: Variant | null) => void;
}) {
  const [price, setPrice] = useState(minorToMajorInput(variant.price, currency));
  const [compareAt, setCompareAt] = useState(
    variant.compareAtPrice === null ? '' : minorToMajorInput(variant.compareAtPrice, currency),
  );
  const [sku, setSku] = useState(variant.sku);
  const [barcode, setBarcode] = useState(variant.barcode ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const nextPrice = parseMoney(price, currency, { allowZero: true });
    const nextCompare = parseOptionalMoney(compareAt, currency);
    const nextSku = normalizeSku(sku);

    if (nextPrice === null) {
      setError(`Enter a price in ${currency}.`);
      return;
    }

    if (nextCompare === undefined) {
      setError('Compare-at is a price, or blank for none.');
      return;
    }

    if (!nextSku) {
      setError('A SKU needs at least one letter or number.');
      return;
    }

    const patch: VariantUpdate = {};

    if (nextPrice !== variant.price) {
      patch.price = nextPrice;
    }

    if (nextCompare !== variant.compareAtPrice) {
      patch.compareAtPrice = nextCompare;
    }

    if (nextSku !== variant.sku) {
      patch.sku = nextSku;
    }

    if ((barcode.trim() || null) !== variant.barcode) {
      patch.barcode = barcode.trim() || null;
    }

    if (!Object.keys(patch).length) {
      onDone(null);
      return;
    }

    setSaving(true);

    try {
      onDone(await apiUpdateVariant(appId, variant.id, patch));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save this variant.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="mt-2.5 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <LabeledInput label={`Price (${currency})`} value={price} onChange={setPrice} mono inputMode="decimal" />
        <LabeledInput
          label="Compare-at"
          value={compareAt}
          onChange={setCompareAt}
          mono
          inputMode="decimal"
          placeholder="None"
        />
        <LabeledInput label="SKU" value={sku} onChange={(v) => setSku(v.toUpperCase())} mono />
        <LabeledInput label="Barcode" value={barcode} onChange={setBarcode} mono placeholder="None" />
      </div>
      {error && <ErrorBanner message={error} />}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={saving} className={BTN_SECONDARY}>
          {saving && <Spinner />}
          {saving ? 'Saving…' : 'Save variant'}
        </button>
        <button type="button" onClick={() => onDone(null)} disabled={saving} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  mono,
  inputMode,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  inputMode?: 'decimal' | 'numeric';
  placeholder?: string;
}) {
  return (
    <label className="block space-y-1">
      <span className="block text-[10px] text-muted">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode={inputMode}
        placeholder={placeholder}
        spellCheck={false}
        className={classNames(mono ? 'ui-input-mono' : 'ui-input', '!py-1.5 !text-[12px]')}
      />
    </label>
  );
}
